import { useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Copy,
  Database,
  Loader2,
  Play,
  Plug,
  Trash2,
} from "lucide-react";
import { api, ApiError } from "../api";
import { StatusBadge } from "../components/StatusBadge";
import { ConnectionCard } from "../components/ConnectionCard";
import { SqlRunner } from "../components/SqlRunner";
import { Countdown } from "../components/Countdown";
import { relativeTime } from "../lib/time";

type Tab = "connection" | "sql";

export function DetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>("connection");
  const [copied, setCopied] = useState(false);

  const {
    data: sandbox,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["sandbox", id],
    queryFn: () => api.getSandbox(id),
    refetchInterval: (query) => {
      const s = query.state.data;
      return s?.status === "provisioning" ? 2_000 : 8_000;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.deleteSandbox(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sandboxes"] });
      navigate("/");
    },
  });

  const onCopyId = async () => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      setTimeout(() => setCopied(false), 900);
    } catch {
      /* ignore */
    }
  };

  const onDelete = () => {
    if (confirm(`Destroy sandbox ${id.slice(0, 8)}? This cannot be undone.`)) {
      deleteMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <div className="py-20 text-center text-sm text-ink-400">
        <span className="caret">loading</span>
      </div>
    );
  }

  if (error || !sandbox) {
    return (
      <div className="max-w-2xl">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-ink-400 hover:text-phosphor-300 mb-6"
        >
          <ArrowLeft size={12} />
          back
        </Link>
        <div className="border border-danger-900 bg-danger-900/20 p-6">
          <div className="term-label !text-danger-400 mb-2">not found</div>
          <pre className="text-sm text-ink-100 whitespace-pre-wrap">
            {error instanceof ApiError ? error.message : "sandbox not found"}
          </pre>
          <button type="button" onClick={() => refetch()} className="btn mt-4">
            retry
          </button>
        </div>
      </div>
    );
  }

  const isReady = sandbox.status === "ready" && !!sandbox.connection;
  const isProvisioning = sandbox.status === "provisioning";
  const isFailed = sandbox.status === "failed";
  const isDestroyed = sandbox.status === "destroyed";

  return (
    <div className="space-y-8">
      <div>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-ink-400 hover:text-phosphor-300 mb-6"
        >
          <ArrowLeft size={12} />
          all sandboxes
        </Link>

        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 mb-3">
              <Database size={14} className="text-phosphor-400" />
              <div className="term-label !text-phosphor-400 before:!hidden !pl-0">
                sandbox
              </div>
              <StatusBadge status={sandbox.status} size="md" />
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-mono text-2xl sm:text-3xl text-ink-100 break-all">
                {sandbox.id}
              </h1>
              <button
                type="button"
                onClick={onCopyId}
                className="text-ink-400 hover:text-phosphor-300"
                aria-label="Copy id"
              >
                {copied ? (
                  <span className="text-[10px] text-signal-400 uppercase tracking-widest">
                    copied
                  </span>
                ) : (
                  <Copy size={14} />
                )}
              </button>
            </div>
            <p className="mt-3 text-sm text-ink-300 font-serif italic leading-relaxed">
              &ldquo;{sandbox.prompt}&rdquo;
            </p>
          </div>

          <button
            type="button"
            onClick={onDelete}
            disabled={deleteMutation.isPending || isDestroyed}
            className="btn btn-danger"
          >
            {deleteMutation.isPending ? (
              <>
                <Loader2 size={13} className="animate-spin" />
                destroying…
              </>
            ) : (
              <>
                <Trash2 size={13} />
                destroy
              </>
            )}
          </button>
        </div>

        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
          <Meta label="engine" value={sandbox.engine} />
          <Meta
            label="created"
            value={relativeTime(sandbox.created_at)}
            title={new Date(sandbox.created_at).toLocaleString()}
          />
          <Meta
            label="auto-destroy"
            value={<Countdown expiresAt={sandbox.expires_at} />}
          />
          <Meta
            label="seed"
            value={sandbox.seed_source ?? "—"}
            title={sandbox.seed_description ?? undefined}
          />
        </div>
      </div>

      {isProvisioning && <ProvisioningState />}

      {isFailed && <FailedState error={sandbox.error} />}

      {isDestroyed && (
        <div className="border border-ink-700 bg-ink-900/60 p-6 text-sm text-ink-400">
          This sandbox has been destroyed. No connection available.
        </div>
      )}

      {isReady && sandbox.connection && (
        <>
          <div className="flex items-center gap-0 border-b border-ink-800">
            <TabButton
              active={tab === "connection"}
              onClick={() => setTab("connection")}
              icon={<Plug size={13} />}
              label="connection"
            />
            <TabButton
              active={tab === "sql"}
              onClick={() => setTab("sql")}
              icon={<Play size={13} />}
              label="sql runner"
            />
          </div>

          <div className="animate-fade-in">
            {tab === "connection" ? (
              <ConnectionCard connection={sandbox.connection} />
            ) : (
              <SqlRunner sandboxId={sandbox.id} />
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Meta({
  label,
  value,
  title,
}: {
  label: string;
  value: ReactNode;
  title?: string;
}) {
  return (
    <div
      className="border border-ink-800 bg-ink-900 px-3 py-2.5"
      title={title}
    >
      <div className="text-[10px] uppercase tracking-widest text-ink-500 mb-1">
        {label}
      </div>
      <div className="text-sm text-ink-100 font-mono truncate">{value}</div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative inline-flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-widest font-medium transition-colors ${
        active
          ? "text-phosphor-400"
          : "text-ink-400 hover:text-ink-200"
      }`}
    >
      {icon}
      {label}
      {active && (
        <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-phosphor-400 shadow-[0_0_8px_var(--color-phosphor-400)]" />
      )}
    </button>
  );
}

function ProvisioningState() {
  return (
    <div className="bracket-frame">
      <span className="bracket-bl" />
      <span className="bracket-br" />
      <div className="flex items-start gap-4">
        <Loader2 className="animate-spin text-phosphor-400 mt-1" size={20} />
        <div>
          <div className="term-label mb-2">provisioning</div>
          <h3 className="font-serif text-2xl text-ink-100 mb-2">
            spinning up your database<span className="caret"></span>
          </h3>
          <p className="text-xs text-ink-400 max-w-md leading-relaxed">
            Pulling image, starting container, waiting for readiness, seeding
            schema & fixtures. Usually takes 10–30 seconds. This page
            auto-refreshes.
          </p>
        </div>
      </div>
    </div>
  );
}

function FailedState({ error }: { error: string | null }) {
  return (
    <div className="border border-danger-900 bg-danger-900/20 p-6">
      <div className="term-label !text-danger-400 mb-2">provisioning failed</div>
      <pre className="text-xs text-ink-100 whitespace-pre-wrap break-words">
        {error ?? "Unknown error. Check server logs."}
      </pre>
    </div>
  );
}
